/**
 * The one place `fetch` is called.
 *
 * Every response goes through `errorFromResponse`, so no caller anywhere has to guess what the
 * body of a failure looks like, and no caller can accidentally treat a 409 as success.
 */

import { ApiError, errorFromResponse, networkError } from "./errors";

/**
 * Inlined at build time and therefore public. Configuration, not a secret — the same rule
 * `NEXT_PUBLIC_*` carries in the landing page. Nothing secret may ever be read from here.
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  /** The access token to present. Omitted for the unauthenticated routes. */
  accessToken?: string | null;
  signal?: AbortSignal;
}

/** Marker for a 204, so a caller reading `void` is reading something deliberate. */
export const NO_CONTENT = Symbol("no-content");

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, accessToken, signal } = options;

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
      // No cookies, in either direction. This app holds its own tokens and sets none, so there is
      // nothing for a browser to attach to a cross-site request and no CSRF surface to defend.
      credentials: "omit",
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw networkError(cause);
  }

  if (!response.ok) throw await errorFromResponse(response);

  if (response.status === 204) return NO_CONTENT as unknown as T;

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError("Athena sent a response this app could not read.", {
      code: "internal_error",
      status: response.status,
    });
  }
}
